import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Upload, Search, Trash2, Sparkles, FileText, X, Save } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const KIND_LABELS = {
  agogi: 'Αγωγή', efesi: 'Έφεση', anairesi: 'Αναίρεση', anakopi: 'Ανακοπή',
  asfalistika: 'Ασφαλιστικά μέτρα', aitisi: 'Αίτηση', protaseis: 'Προτάσεις',
  prosthiki: 'Προσθήκη - Αντίκρουση', exodiko: 'Εξώδικο', diatagi_pliromis: 'Διαταγή πληρωμής',
  prosfygi: 'Προσφυγή', minysi: 'Μήνυση / Έγκληση', ypomnima: 'Υπόμνημα',
  klisi: 'Κλήση', gnomodotisi: 'Γνωμοδότηση', other: 'Άλλο',
};
const PROC_LABELS = { civil: 'Πολιτική', criminal: 'Ποινική', administrative: 'Διοικητική', extrajudicial: 'Εξωδικαστική' };

export default function PleadingsLibrary() {
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const cancelRef = useRef(false);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [unclassified, setUnclassified] = useState(0);
  const [q, setQ] = useState('');
  const [deep, setDeep] = useState(false);
  const [kind, setKind] = useState('');
  const [procedure, setProcedure] = useState('');
  const [uploading, setUploading] = useState(null); // {done,total,errors,dups}
  const [classifying, setClassifying] = useState(null); // {done,remaining}
  const [expanded, setExpanded] = useState(null); // detail doc
  const [edit, setEdit] = useState({});
  const [loading, setLoading] = useState(false);

  const fetchList = useCallback(async (reset = true, skipOverride = null) => {
    setLoading(true);
    try {
      const params = { q, kind, procedure, deep: deep ? 1 : 0, skip: skipOverride ?? 0, limit: 50 };
      const res = await axios.get(`${API}/pleadings`, { params });
      setTotal(res.data.total);
      setUnclassified(res.data.unclassified);
      setItems((prev) => (reset ? res.data.items : [...prev, ...res.data.items]));
    } catch (e) {
      if (e.response?.status === 403) {
        toast.error('Πρόσβαση μόνο για διαχειριστή');
        navigate('/dashboard');
      } else toast.error('Σφάλμα φόρτωσης βιβλιοθήκης');
    } finally {
      setLoading(false);
    }
  }, [q, kind, procedure, deep, navigate]);

  useEffect(() => {
    const t = setTimeout(() => fetchList(true), 350);
    return () => clearTimeout(t);
  }, [fetchList]);

  const handleFiles = async (fileList) => {
    const files = Array.from(fileList || []).filter((f) => /\.(docx?|DOCX?)$/.test(f.name));
    if (!files.length) { toast.error('Επιλέξτε αρχεία Word (.docx)'); return; }
    cancelRef.current = false;
    const prog = { done: 0, total: files.length, errors: [], dups: 0 };
    setUploading({ ...prog });
    const CHUNK = 3;
    for (let i = 0; i < files.length; i += CHUNK) {
      if (cancelRef.current) break;
      const batch = files.slice(i, i + CHUNK);
      await Promise.all(batch.map(async (f) => {
        const fd = new FormData();
        fd.append('file', f);
        try {
          const res = await axios.post(`${API}/pleadings/upload`, fd);
          if (res.data.duplicate) prog.dups += 1;
        } catch (e) {
          prog.errors.push(`${f.name}: ${e.response?.data?.detail || 'σφάλμα'}`);
        } finally {
          prog.done += 1;
          setUploading({ ...prog });
        }
      }));
    }
    setUploading({ ...prog, finished: true });
    toast.success(`Ολοκληρώθηκε: ${prog.done - prog.errors.length}/${prog.total} αρχεία${prog.dups ? ` (${prog.dups} διπλότυπα παραλείφθηκαν)` : ''}`);
    fetchList(true);
  };

  const runBatchClassify = async () => {
    cancelRef.current = false;
    let done = 0;
    let remaining = unclassified;
    setClassifying({ done, remaining });
    while (remaining > 0 && !cancelRef.current) {
      try {
        const res = await axios.post(`${API}/pleadings/ai-classify-batch`, { limit: 5 });
        done += res.data.classified;
        remaining = res.data.remaining;
        setClassifying({ done, remaining });
        if (res.data.errors?.length && res.data.classified === 0) {
          toast.error(`Σφάλμα AI: ${res.data.errors[0].error}`);
          break;
        }
      } catch (e) {
        toast.error('Σφάλμα AI ταξινόμησης');
        break;
      }
    }
    setClassifying(null);
    toast.success(`AI ταξινόμηση: ${done} δικόγραφα`);
    fetchList(true);
  };

  const classifyOne = async (pid) => {
    try {
      await axios.post(`${API}/pleadings/${pid}/ai-classify`);
      toast.success('Ταξινομήθηκε με AI');
      fetchList(true);
      if (expanded?.id === pid) openDetail(pid);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Σφάλμα AI ταξινόμησης');
    }
  };

  const openDetail = async (pid) => {
    try {
      const res = await axios.get(`${API}/pleadings/${pid}`);
      setExpanded(res.data);
      setEdit({
        title: res.data.title || '', doc_kind: res.data.doc_kind || 'other',
        procedure_type: res.data.procedure_type || '', court: res.data.court || '',
        year: res.data.year || '', summary: res.data.summary || '',
      });
    } catch { toast.error('Σφάλμα φόρτωσης'); }
  };

  const saveEdit = async () => {
    try {
      const body = { ...edit, year: edit.year ? parseInt(edit.year, 10) : null };
      Object.keys(body).forEach((k) => { if (body[k] === '' || body[k] === null) delete body[k]; });
      await axios.put(`${API}/pleadings/${expanded.id}`, body);
      toast.success('Αποθηκεύτηκε');
      setExpanded(null);
      fetchList(true);
    } catch { toast.error('Σφάλμα αποθήκευσης'); }
  };

  const deleteOne = async (pid) => {
    if (!window.confirm('Διαγραφή δικογράφου από τη βιβλιοθήκη;')) return;
    try {
      await axios.delete(`${API}/pleadings/${pid}`);
      toast.success('Διαγράφηκε');
      if (expanded?.id === pid) setExpanded(null);
      fetchList(true);
    } catch { toast.error('Σφάλμα διαγραφής'); }
  };

  return (
    <div className="min-h-screen bg-[#f7f2e7]" data-testid="pleadings-library-page">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-20">
        <div className="max-w-6xl mx-auto px-3 sm:px-6 py-3 flex items-center gap-2 sm:gap-3 flex-wrap">
          <button data-testid="pleadings-back-btn" onClick={() => navigate('/dashboard')} title="Επιστροφή"
            className="flex items-center justify-center w-9 h-9 rounded-lg border border-slate-300 bg-white text-slate-600 hover:bg-slate-50">
            <ArrowLeft className="w-4 h-4" />
          </button>
          <h1 className="text-base sm:text-xl font-semibold text-slate-900 tracking-tight flex items-center gap-2">
            <FileText className="w-5 h-5 text-red-700" />
            Βιβλιοθήκη Δικογράφων
          </h1>
          <span data-testid="pleadings-total-badge" className="text-xs font-medium text-slate-500 bg-slate-100 rounded-full px-2.5 py-1">
            {total} δικόγραφα
          </span>
          <div className="ml-auto flex items-center gap-2">
            {unclassified > 0 && !classifying && (
              <Button data-testid="pleadings-ai-all-btn" onClick={runBatchClassify}
                className="h-9 bg-slate-900 text-white hover:bg-slate-800 text-sm">
                <Sparkles className="w-4 h-4 mr-1.5" />
                AI ταξινόμηση ({unclassified})
              </Button>
            )}
            <Button data-testid="pleadings-upload-btn" onClick={() => fileRef.current?.click()}
              className="h-9 bg-red-700 text-white hover:bg-red-800 text-sm">
              <Upload className="w-4 h-4 mr-1.5" />
              Ανέβασμα Word
            </Button>
            <input ref={fileRef} data-testid="pleadings-file-input" type="file" multiple accept=".doc,.docx"
              className="hidden" onChange={(e) => { handleFiles(e.target.files); e.target.value = ''; }} />
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-3 sm:px-6 py-4 sm:py-6 space-y-4">
        {uploading && (
          <div data-testid="upload-progress" className="bg-white border border-slate-200 rounded-xl p-4 space-y-2">
            <div className="flex items-center justify-between text-sm font-medium text-slate-800">
              <span>Ανέβασμα: {uploading.done}/{uploading.total}{uploading.dups ? ` · ${uploading.dups} διπλότυπα` : ''}</span>
              <div className="flex items-center gap-2">
                {!uploading.finished && (
                  <button onClick={() => { cancelRef.current = true; }} className="text-xs text-red-700 hover:underline">Διακοπή</button>
                )}
                {uploading.finished && (
                  <button data-testid="upload-progress-close" onClick={() => setUploading(null)} className="text-slate-400 hover:text-slate-600"><X className="w-4 h-4" /></button>
                )}
              </div>
            </div>
            <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
              <div className="h-full bg-red-700 transition-all" style={{ width: `${(uploading.done / uploading.total) * 100}%` }} />
            </div>
            {uploading.errors.length > 0 && (
              <div className="text-xs text-red-600 max-h-24 overflow-y-auto">
                {uploading.errors.map((er, i) => <div key={i}>{er}</div>)}
              </div>
            )}
          </div>
        )}

        {classifying && (
          <div data-testid="classify-progress" className="bg-white border border-slate-200 rounded-xl p-4 flex items-center justify-between text-sm">
            <span className="font-medium text-slate-800 flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-red-700 animate-pulse" />
              AI ταξινόμηση: {classifying.done} έτοιμα · {classifying.remaining} απομένουν
            </span>
            <button onClick={() => { cancelRef.current = true; }} className="text-xs text-red-700 hover:underline">Διακοπή</button>
          </div>
        )}

        <div className="bg-white border border-slate-200 rounded-xl p-3 flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input data-testid="pleadings-search-input" value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Αναζήτηση (τίτλος, δικαστήριο, ετικέτες...)" className="pl-9 h-9" />
          </div>
          <label className="flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer">
            <input data-testid="pleadings-deep-toggle" type="checkbox" checked={deep} onChange={(e) => setDeep(e.target.checked)} />
            και στο κείμενο
          </label>
          <Select value={kind || 'all'} onValueChange={(v) => setKind(v === 'all' ? '' : v)}>
            <SelectTrigger data-testid="pleadings-kind-filter" className="w-[180px] h-9"><SelectValue placeholder="Είδος" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Όλα τα είδη</SelectItem>
              {Object.entries(KIND_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={procedure || 'all'} onValueChange={(v) => setProcedure(v === 'all' ? '' : v)}>
            <SelectTrigger data-testid="pleadings-procedure-filter" className="w-[160px] h-9"><SelectValue placeholder="Διαδικασία" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Όλες οι διαδικασίες</SelectItem>
              {Object.entries(PROC_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2" data-testid="pleadings-list">
          {items.length === 0 && !loading && (
            <div className="bg-white border border-dashed border-slate-300 rounded-xl p-10 text-center text-slate-500 text-sm" data-testid="pleadings-empty">
              Η βιβλιοθήκη είναι κενή. Πατήστε «Ανέβασμα Word» και επιλέξτε μαζικά τα δικόγραφά σας (.docx).
            </div>
          )}
          {items.map((it) => (
            <div key={it.id} data-testid={`pleading-row-${it.id}`}
              className="bg-white border border-slate-200 rounded-xl p-3 sm:p-4 hover:border-slate-300 transition-colors">
              <div className="flex items-start gap-3">
                <button className="flex-1 text-left" data-testid={`pleading-open-${it.id}`} onClick={() => openDetail(it.id)}>
                  <p className="text-sm font-semibold text-slate-900">{it.title || it.filename}</p>
                  <p className="text-xs text-slate-500 mt-0.5">{it.filename} · {(it.size / 1024).toFixed(0)} KB</p>
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {it.doc_kind && <span className="text-[11px] font-medium bg-red-50 text-red-700 border border-red-200 rounded-full px-2 py-0.5">{KIND_LABELS[it.doc_kind] || it.doc_kind}</span>}
                    {it.procedure_type && <span className="text-[11px] font-medium bg-slate-100 text-slate-700 rounded-full px-2 py-0.5">{PROC_LABELS[it.procedure_type]}</span>}
                    {it.year ? <span className="text-[11px] font-medium bg-slate-100 text-slate-700 rounded-full px-2 py-0.5">{it.year}</span> : null}
                    {it.court && <span className="text-[11px] text-slate-500">{it.court}</span>}
                    {it.ai_classified && <span className="text-[11px] font-medium bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full px-2 py-0.5">AI ✓</span>}
                    {it.extract_error && <span className="text-[11px] text-amber-600">χωρίς κείμενο</span>}
                  </div>
                </button>
                <div className="flex items-center gap-1 shrink-0">
                  {!it.ai_classified && it.text_chars > 0 && (
                    <button data-testid={`pleading-ai-btn-${it.id}`} onClick={() => classifyOne(it.id)} title="AI ταξινόμηση"
                      className="w-8 h-8 rounded-lg border border-slate-300 bg-white text-slate-500 hover:text-red-700 flex items-center justify-center">
                      <Sparkles className="w-4 h-4" />
                    </button>
                  )}
                  <button data-testid={`pleading-delete-btn-${it.id}`} onClick={() => deleteOne(it.id)} title="Διαγραφή"
                    className="w-8 h-8 rounded-lg border border-slate-300 bg-white text-slate-500 hover:text-red-600 flex items-center justify-center">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
          {items.length < total && (
            <Button data-testid="pleadings-load-more" variant="outline" className="w-full h-9 text-sm"
              onClick={() => fetchList(false, items.length)}>
              Φόρτωση περισσότερων ({items.length}/{total})
            </Button>
          )}
        </div>
      </main>

      {expanded && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-start sm:items-center justify-center p-3 overflow-y-auto" onClick={() => setExpanded(null)}>
          <div data-testid="pleading-detail-modal" className="bg-white rounded-2xl w-full max-w-3xl p-4 sm:p-6 space-y-4 my-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between gap-2">
              <h2 className="text-base sm:text-lg font-semibold text-slate-900">{expanded.filename}</h2>
              <button data-testid="pleading-detail-close" onClick={() => setExpanded(null)} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2">
                <Label>Τίτλος</Label>
                <Input data-testid="pleading-edit-title" value={edit.title} onChange={(e) => setEdit({ ...edit, title: e.target.value })} className="h-9 mt-1" />
              </div>
              <div>
                <Label>Είδος</Label>
                <Select value={edit.doc_kind} onValueChange={(v) => setEdit({ ...edit, doc_kind: v })}>
                  <SelectTrigger data-testid="pleading-edit-kind" className="h-9 mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(KIND_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Διαδικασία</Label>
                <Select value={edit.procedure_type || 'none'} onValueChange={(v) => setEdit({ ...edit, procedure_type: v === 'none' ? '' : v })}>
                  <SelectTrigger data-testid="pleading-edit-procedure" className="h-9 mt-1"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">—</SelectItem>
                    {Object.entries(PROC_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Δικαστήριο</Label>
                <Input data-testid="pleading-edit-court" value={edit.court} onChange={(e) => setEdit({ ...edit, court: e.target.value })} className="h-9 mt-1" />
              </div>
              <div>
                <Label>Έτος</Label>
                <Input data-testid="pleading-edit-year" type="number" value={edit.year} onChange={(e) => setEdit({ ...edit, year: e.target.value })} className="h-9 mt-1" />
              </div>
            </div>
            {expanded.summary && <p className="text-sm text-slate-600 bg-slate-50 rounded-lg p-3">{expanded.summary}</p>}
            {(expanded.legal_basis || []).length > 0 && (
              <p className="text-xs text-slate-500">Νομική βάση: {expanded.legal_basis.join(' · ')}</p>
            )}
            {(expanded.tags || []).length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {expanded.tags.map((t, i) => <span key={i} className="text-[11px] bg-slate-100 text-slate-600 rounded-full px-2 py-0.5">{t}</span>)}
              </div>
            )}
            {expanded.text_preview ? (
              <div data-testid="pleading-text-preview" className="border border-slate-200 rounded-lg p-3 max-h-64 overflow-y-auto text-xs text-slate-700 whitespace-pre-wrap">
                {expanded.text_preview}
              </div>
            ) : (
              <p className="text-xs text-amber-600">{expanded.extract_error || 'Δεν υπάρχει διαθέσιμο κείμενο.'}</p>
            )}
            <div className="flex flex-wrap items-center gap-2 justify-end">
              {!expanded.ai_classified && expanded.text_chars > 0 && (
                <Button variant="outline" className="h-9 text-sm" data-testid="pleading-detail-ai-btn" onClick={() => classifyOne(expanded.id)}>
                  <Sparkles className="w-4 h-4 mr-1.5" />AI ταξινόμηση
                </Button>
              )}
              <Button data-testid="pleading-detail-save-btn" onClick={saveEdit} className="h-9 bg-red-700 text-white hover:bg-red-800 text-sm">
                <Save className="w-4 h-4 mr-1.5" />Αποθήκευση
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
